import React, { useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { API_BASE_URL } from '../services/api';
import './StripeElements.css';

const StripeElements = forwardRef(({ onPaymentSuccess, onPaymentError, orderData, isGuestMode = false, showPayButton = true }, ref) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const formRef = useRef(null);

  // 国家代码转换函数
  const convertCountryCode = (country) => {
    const countryMap = {
      'United States': 'US',
      'United Kingdom': 'GB',
      'Canada': 'CA',
      'Australia': 'AU',
      'Germany': 'DE',
      'France': 'FR',
      'Japan': 'JP',
      'China': 'CN',
      'South Korea': 'KR',
      'India': 'IN'
    };
    
    return countryMap[country] || country;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    const cardElement = elements.getElement(CardElement);

    try {
      // 检查用户是否已登录（guest mode 不需要 token）
      const token = localStorage.getItem('token');
      if (!isGuestMode && !token) {
        throw new Error('Please log in to continue with payment');
      }

      // 创建请求头（guest mode 不发送 Authorization header）
      const headers = {
        'Content-Type': 'application/json'
      };
      
      // 如果有 token，添加 Authorization header
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // 创建支付Intent
      const response = await fetch(`${API_BASE_URL}/payments/create-intent`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(orderData)
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Please log in again to continue with payment');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to create payment intent');
      }

      const { clientSecret } = result.data;

      // 确认支付
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: orderData.shippingAddress.firstName + ' ' + orderData.shippingAddress.lastName,
            email: orderData.email,
            address: {
              line1: orderData.shippingAddress.address,
              city: orderData.shippingAddress.city,
              state: orderData.shippingAddress.state,
              postal_code: orderData.shippingAddress.postalCode,
              country: convertCountryCode(orderData.shippingAddress.country)
            }
          }
        }
      });

      if (error) {
        setError(error.message);
        onPaymentError(error.message);
      } else if (paymentIntent?.status === 'succeeded') {
        onPaymentSuccess(paymentIntent);
      } else {
        // 既没 error 也没 succeeded（比如 requires_action、processing、被取消）。
        // 以前这里什么都不做：不建单、不报错、按钮恢复可点 ——
        // 用户可能已经被扣款，却看不到任何反馈。
        const status = paymentIntent?.status || 'unknown';
        const msg = status === 'processing'
          ? 'Your payment is still processing. Please wait a moment and check your email for the confirmation.'
          : `Payment was not completed (status: ${status}). You have not been charged, please try again.`;
        setError(msg);
        onPaymentError(msg);
      }
    } catch (err) {
      setError(err.message);
      onPaymentError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  // 暴露submit方法给父组件
  useImperativeHandle(ref, () => ({
    submit: async () => {
      if (stripe && elements && !processing) {
        const fakeEvent = { preventDefault: () => {} };
        await handleSubmit(fakeEvent);
      }
    },
    processing,
    stripe
  }), [stripe, elements, processing]);

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#000',
        fontFamily: 'Inter, system-ui, sans-serif',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  // 如果Stripe还没有加载完成，显示加载状态
  if (!stripe || !elements) {
    return (
      <div className="stripe-loading">
        <div className="card-element-container">
          <div style={{ padding: '12px', color: '#666' }}>Loading payment form...</div>
        </div>
      </div>
    );
  }

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="stripe-form">
      <div className="card-element-container">
        <CardElement 
          options={cardElementOptions}
        />
      </div>
      
      {error && (
        <div className="payment-error">
          {error}
        </div>
      )}
      
      {showPayButton && (
        <button 
          type="submit" 
          disabled={!stripe || processing}
          className="pay-button"
        >
          {processing ? 'Processing...' : `Pay $${orderData.total.toFixed(2)}`}
        </button>
      )}
    </form>
  );
});

StripeElements.displayName = 'StripeElements';

export default StripeElements;
